/**
 * Ammunition is stuff — the sector side (IDEAS X20). Behind the sector rule `ammunition`, a squad carries a finite
 * number of rounds per unit for its standard launcher, bought from supply at a rest point (56.11's refit row); it
 * crosses down to a turn-clock battle as every unit's standard ammo count (today unlimited), what the living still
 * carry comes back, and a unit that ran dry is a shortage the report names. The action clock's magazines are lane
 * C's (Phase 65); this is the quantity the sector keeps. Pure.
 */
import type { GameState } from '../types';
import type { BattleOutcome, BattleSetup, FactionId } from './types';

/** A unit's standard rounds left at the end, or null when unlimited (the rule off). */
export function standardLeft(p: GameState['players'][number]): number | null {
  const n = p.loadout.ammo.standard;
  return n === null || n === undefined ? null : n;
}

/** Units of each side that ran dry — alive or dead, having had a finite count and none left. */
export function shortageOf(setup: BattleSetup, finalState: GameState): BattleOutcome['shortage'] {
  const out: NonNullable<BattleOutcome['shortage']> = {};
  setup.sides.forEach((side, t) => {
    const dry = finalState.players.filter(p => p.teamIndex === t && standardLeft(p) === 0).length;
    if (dry > 0) out[side.faction] = dry;
  });
  return Object.keys(out).length ? out : undefined;
}

/** The mean standard rounds the living of each side still carry, or nothing when unlimited. */
export function ammoLeftOf(setup: BattleSetup, finalState: GameState): Partial<Record<FactionId, number>> | undefined {
  const out: Partial<Record<FactionId, number>> = {};
  setup.sides.forEach((side, t) => {
    const living = finalState.players.filter(p => p.teamIndex === t && (p.alive || p.extracted));
    const counts = living.map(standardLeft).filter((n): n is number => n !== null);
    if (counts.length) out[side.faction] = Math.round(counts.reduce((a, n) => a + n, 0) / counts.length);
  });
  return Object.keys(out).length ? out : undefined;
}
